import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2 } from 'lucide-react';
import { DAYS } from './NoteGrid';

const Field = ({ label, help, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
    {help && <p className="text-[10px] text-muted-foreground mt-0.5">{help}</p>}
  </label>
);
const inputCls = 'w-full bg-secondary border border-border rounded-sm px-2 py-1 text-xs text-foreground focus:outline-none focus:border-blue-500';

const ExtraField = ({ spec, value, onChange, testid }) => {
  if (spec.type === 'bool') return <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />;
  if (spec.type === 'select') return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)} className={inputCls} data-testid={testid}>
      {(spec.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  if (spec.type === 'textarea' || spec.type === 'list') return (
    <textarea rows={3} value={spec.type === 'list' ? (Array.isArray(value) ? value.join('\n') : value || '') : (value ?? '')}
      onChange={e => onChange(spec.type === 'list' ? e.target.value.split('\n').map(s => s.trim()).filter(Boolean) : e.target.value)}
      className={inputCls + ' font-mono'} data-testid={testid} placeholder={spec.type === 'list' ? 'ein Eintrag pro Zeile' : ''} />
  );
  if (spec.type === 'int') return <input type="number" min={spec.min ?? undefined} max={spec.max ?? undefined} value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} className={inputCls + ' font-mono'} data-testid={testid} />;
  if (spec.type === 'time') return <input type="time" value={value ?? ''} onChange={e => onChange(e.target.value)} className={inputCls + ' font-mono'} data-testid={testid} />;
  return <input type="text" value={value ?? ''} onChange={e => onChange(e.target.value)} className={inputCls} data-testid={testid} />;
};

// Per-board settings: display time, days, window, exclusive/order (rotation) or priority (event) + extra settings.
export const BoardSettingsForm = ({ board, onSaved }) => {
  const s = board.settings;
  const [form, setForm] = useState({
    display_minutes: Math.max(1, Math.round((s.display_seconds || 300) / 60)),
    days: s.days || [], window: s.window || null, exclusive: !!s.exclusive, priority: s.priority ?? 5,
    ignore_office_hours: !!s.ignore_office_hours, until_resolved: !!s.until_resolved,
    max_display_minutes: Math.round((s.max_display_seconds || 0) / 60), extra: { ...(s.extra || {}) },
  });
  const [saving, setSaving] = useState(false);
  const isRot = board.kind === 'rotation';
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const body = {
      display_seconds: Math.max(1, Number(form.display_minutes)) * 60,
      days: form.days.length ? form.days : null,
      window: form.window && form.window.start && form.window.end ? form.window : null,
      extra: form.extra,
      ...(isRot ? { exclusive: form.exclusive } : {
        priority: Number(form.priority), ignore_office_hours: form.ignore_office_hours,
        until_resolved: form.until_resolved, max_display_seconds: Math.max(0, Number(form.max_display_minutes)) * 60,
      }),
    };
    try { await api.vbManagerSaveBoard(board.id, body); toast.success(`${board.name}: Einstellungen gespeichert`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  return (
    <div className="mt-3 border-t border-border pt-3 space-y-3" data-testid={`vb-board-settings-${board.id}`}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Anzeigezeit" help={isRot ? 'pro Durchgang (mehrere Frames teilen sie sich)' : 'wie lange das Event gezeigt wird'}>
          <div className="flex items-center gap-1.5">
            <input type="number" min="1" max="1440" value={form.display_minutes} onChange={e => set('display_minutes', e.target.value)} className={inputCls + ' font-mono'} data-testid={`vb-display-${board.id}`} />
            <span className="text-[10px] text-muted-foreground">Min</span>
          </div>
        </Field>
        {isRot ? (
          <Field label="Exklusiv im Zeitfenster" help="nur dieses Board, solange Tage/Fenster passen">
            <Switch checked={form.exclusive} onCheckedChange={v => set('exclusive', v)} data-testid={`vb-exclusive-${board.id}`} />
          </Field>
        ) : (
          <Field label="Priorität" help="1 = höchste, unterbricht niedrigere">
            <input type="number" min="1" max="99" value={form.priority} onChange={e => set('priority', e.target.value)} className={inputCls + ' font-mono'} data-testid={`vb-priority-${board.id}`} />
          </Field>
        )}
      </div>
      <Field label="Tage" help="keine Auswahl = jeden Tag">
        <div className="flex items-center gap-1 flex-wrap">
          {DAYS.map((d, i) => (
            <button key={d} type="button" onClick={() => set('days', form.days.includes(i) ? form.days.filter(x => x !== i) : [...form.days, i].sort())} data-testid={`vb-day-${board.id}-${i}`}
              className={`text-[10px] px-2 py-1 rounded-sm border ${form.days.includes(i) ? 'bg-amber-500/15 text-amber-300 border-amber-500/40' : 'text-muted-foreground border-border'}`}>{d}</button>
          ))}
        </div>
      </Field>
      <Field label="Zeitfenster" help="leer = ganztags">
        <div className="flex items-center gap-1.5">
          <input type="time" value={form.window?.start || ''} onChange={e => set('window', { ...(form.window || {}), start: e.target.value })} className={inputCls + ' font-mono w-28'} data-testid={`vb-window-start-${board.id}`} />
          <span className="text-[10px] text-muted-foreground">–</span>
          <input type="time" value={form.window?.end || ''} onChange={e => set('window', { ...(form.window || {}), end: e.target.value })} className={inputCls + ' font-mono w-28'} data-testid={`vb-window-end-${board.id}`} />
          {form.window && <button type="button" onClick={() => set('window', null)} className="text-[10px] text-muted-foreground hover:text-foreground">löschen</button>}
        </div>
      </Field>
      {!isRot && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Auch außerhalb der Bürozeit" help="Event wartet sonst bis zum nächsten Bürotag">
            <Switch checked={form.ignore_office_hours} onCheckedChange={v => set('ignore_office_hours', v)} data-testid={`vb-ignore-office-${board.id}`} />
          </Field>
          <Field label="Bis erledigt" help="bleibt, bis das Board es als erledigt meldet">
            <div className="flex items-center gap-2">
              <Switch checked={form.until_resolved} onCheckedChange={v => set('until_resolved', v)} data-testid={`vb-until-resolved-${board.id}`} />
              {form.until_resolved && <><input type="number" min="0" value={form.max_display_minutes} onChange={e => set('max_display_minutes', e.target.value)} className={inputCls + ' font-mono w-20'} data-testid={`vb-max-display-${board.id}`} /><span className="text-[10px] text-muted-foreground">Min max (0 = ∞)</span></>}
            </div>
          </Field>
        </div>
      )}
      {board.extra_settings?.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {board.extra_settings.map(spec => (
            <Field key={spec.key} label={spec.label} help={spec.help}>
              <ExtraField spec={spec} value={form.extra[spec.key]} onChange={v => set('extra', { ...form.extra, [spec.key]: v })} testid={`vb-extra-${board.id}-${spec.key}`} />
            </Field>
          ))}
        </div>
      )}
      <Button size="sm" onClick={save} disabled={saving} className="h-7 text-xs bg-blue-600 hover:bg-blue-500 text-white" data-testid={`vb-save-settings-${board.id}`}>
        {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Einstellungen speichern
      </Button>
    </div>
  );
};
